import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { ContractsService } from '../../../services/contracts.service';
import { CustomersService } from '../../../services/customers.service';
import { PaymentService } from '../../../services/payment.service';
import { AccountsReceivableService } from '../../../services/accounts-receivable.service';
import { ErrorService } from '../../../shared/services/error.service';
import { ViaCepService, ViaCepResponse } from '../../../services/viacep.service';
import { Customer } from '../../../models/Customer';
import { AsaasCharge, AsaasChargeBatch, AsaasCustomer } from '../../../models/Payment';
import { AccountsReceivableCreate } from '../../../models/AccountsReceivable';
import { B2bContractDetail } from '../../../models/Contract';

@Component({
  selector: 'app-b2b-contracts-form',
  templateUrl: './b2b-contracts-form.component.html',
  styleUrls: ['./b2b-contracts-form.component.css']
})
export class B2bContractsFormComponent implements OnInit {
  contractForm: FormGroup;
  isSubmitting = false;
  isLoadingEdit = false;
  isEditMode = false;
  contractId: number | null = null;
  /** Snapshot do contrato carregado na edição (preserva URLs/status não editáveis no form). */
  private loadedContract: B2bContractDetail | null = null;
  currentStep = 1;
  readonly totalSteps = 1;
  currentYear = new Date().getFullYear();
  minDate: string;
  isLoadingCep = false;
  cepError: string | null = null;

  companySearchTerm = '';
  companySearchResults: Customer[] = [];
  isLoadingCompanySearch = false;

  paymentMethods = [
    { value: 'CASH', label: 'Dinheiro' },
    { value: 'PIX', label: 'Pix' },
    { value: 'BOLETO', label: 'Boleto' },
    { value: 'CREDIT_CARD', label: 'Cartão' }
  ];
  depositPayments = [
    { method: 'CASH', amount: 0 },
    { method: 'PIX', amount: 0 }
  ];
  firstInstallmentPayments = [
    { method: 'CASH', amount: 0 },
    { method: 'PIX', amount: 0 }
  ];
  depositPaymentMode: 'single' | 'split' = 'single';
  firstInstallmentPaymentMode: 'single' | 'split' = 'single';
  depositInstallments = 1;

  asaasCustomerId: string | null = null;
  asaasCustomerLoading = false;
  asaasCustomerError: string | null = null;
  asaasCustomerInfo: AsaasCustomer | null = null;

  depositCardInstallments = 1;
  creditCardHolderName = '';
  creditCardNumber = '';
  creditCardExpiryMonth = '';
  creditCardExpiryYear = '';
  creditCardCcv = '';
  depositCardInstallments2 = 1;
  creditCardHolderName2 = '';
  creditCardNumber2 = '';
  creditCardExpiryMonth2 = '';
  creditCardExpiryYear2 = '';
  creditCardCcv2 = '';

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private contractsService: ContractsService,
    private customersService: CustomersService,
    private paymentService: PaymentService,
    private accountsReceivableService: AccountsReceivableService,
    private errorService: ErrorService,
    private viaCepService: ViaCepService
  ) {
    // Data mínima = hoje
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.minDate = today.toISOString().split('T')[0];
    
    this.contractForm = this.createForm();
  }

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const editId = idParam ? Number(idParam) : NaN;
    this.isEditMode = Number.isFinite(editId) && editId > 0;
    this.contractId = this.isEditMode ? editId : null;

    if (this.isEditMode) {
      this.loadContractForEdit(editId);
    } else {
      this.generateContractNumber();
    }
    
    // Validação de data de término quando data de início mudar (não necessário mais, mas mantido para segurança)
    this.contractForm.get('startDate')?.valueChanges.subscribe(() => {
      this.contractForm.get('endDate')?.updateValueAndValidity();
    });

    this.contractForm.get('cnpj')?.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((value: string) => this.onCnpjChange(value));

    this.contractForm.get('baseAmount')?.valueChanges.subscribe(() => this.recalculateFirstInstallmentSplit());
    this.contractForm.get('depositAmount')?.valueChanges.subscribe(() => this.recalculateDepositSplit());
    this.initPaymentSplits(0, 0);
  }

  private loadContractForEdit(contractId: number): void {
    this.isLoadingEdit = true;
    this.contractsService.getB2bContractById(contractId).subscribe({
      next: (contract) => {
        this.loadedContract = contract;
        this.patchFormFromContract(contract);
        this.isLoadingEdit = false;
      },
      error: () => {
        this.isLoadingEdit = false;
        this.router.navigate(['/b2b/contracts']);
      }
    });
  }

  private patchFormFromContract(contract: B2bContractDetail): void {
    const start = contract.startDate ? new Date(contract.startDate) : null;
    const end = contract.endDate ? new Date(contract.endDate) : null;
    this.contractForm.patchValue({
      customerId: contract.customerId || 1,
      documentTemplateVersionId: contract.documentTemplateVersionId || 1,
      contractNumber: contract.contractNumber || '',
      contractName: contract.contractName || '',
      startDate: start && !Number.isNaN(start.getTime()) ? this.formatDateToBr(start) : '',
      endDate: end && !Number.isNaN(end.getTime()) ? this.formatDateToBr(end) : '',
      status: contract.status || 'Draft',
      cnpj: contract.cnpj || '',
      companyName: contract.companyName || '',
      address: contract.companyAddress || '',
      notes: contract.observations || '',
      depositAmount: contract.depositAmount ?? 0
    });

    // Em edição, permitir alterar a data de início (já não é "hoje" fixo)
    this.contractForm.get('startDate')?.enable({ emitEvent: false });
  }

  createForm(): FormGroup {
    // Data de início sempre = hoje (formato brasileiro DD/MM/YYYY)
    const todayBr = this.formatDateToBr(new Date());
    
    return this.fb.group({
      customerId: [1, [Validators.required]], // Definido ao selecionar empresa na busca (pessoa jurídica) ou 1 por padrão
      documentTemplateVersionId: [1, [Validators.required]], // Fixo como 1 (não aparece no front)
      contractNumber: [{ value: '', disabled: true }, [Validators.required]], // Desabilitado, gerado automaticamente
      contractName: ['', [Validators.required]],
      startDate: [{ value: todayBr, disabled: true }, [Validators.required]], // Fixo como hoje (formato BR)
      endDate: ['', [Validators.required, this.endDateValidator]],
      status: ['Aguardando Assinatura', [Validators.required]], // Fixo
      cnpj: ['', [Validators.required]],
      companyName: ['', [Validators.required]],
      tradeName: [''],
      address: [''],
      city: [''],
      state: [''],
      zipCode: [''],
      phoneNumber: [''],
      email: ['', [Validators.email]],
      representativeName: [''],
      representativeCpf: [''],
      representativeRole: [''],
      notes: [''],
      baseAmount: [0, [Validators.min(0)]],
      depositAmount: [0, [Validators.min(0)]]
    });
  }

  // Validador: data de início não pode ser retroativa
  minDateValidator = (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;
    const selectedDate = new Date(control.value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);
    if (selectedDate < today) {
      return { minDate: true };
    }
    return null;
  };

  // Validador: data de término deve ser maior que data de início
  endDateValidator = (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;
    const startDateBr = this.contractForm?.get('startDate')?.value;
    if (!startDateBr) return null;
    
    // Converter datas do formato brasileiro para Date
    const endDate = this.parseBrDateToDate(control.value);
    const start = this.parseBrDateToDate(startDateBr);
    
    if (!endDate || !start) {
      return { invalidDate: true };
    }
    
    endDate.setHours(0, 0, 0, 0);
    start.setHours(0, 0, 0, 0);
    if (endDate <= start) {
      return { endDateBeforeStart: true };
    }
    return null;
  };

  // Converter data do formato brasileiro (DD/MM/YYYY) para Date
  parseBrDateToDate(dateBr: string): Date | null {
    if (!dateBr) return null;
    const parts = dateBr.split('/');
    if (parts.length !== 3) return null;
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // Mês começa em 0
    const year = parseInt(parts[2], 10);
    if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
    return new Date(year, month, day);
  }

  // Converter Date para formato brasileiro (DD/MM/YYYY)
  formatDateToBr(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  // Formatar input de data para formato brasileiro (DD/MM/YYYY)
  formatDate(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, ''); // Remove tudo que não é dígito
    
    if (value.length > 8) {
      value = value.substring(0, 8);
    }
    
    // Aplicar máscara DD/MM/YYYY
    if (value.length > 6) {
      value = value.substring(0, 2) + '/' + value.substring(2, 4) + '/' + value.substring(4, 8);
    } else if (value.length > 4) {
      value = value.substring(0, 2) + '/' + value.substring(2, 4) + '/' + value.substring(4, 6);
    } else if (value.length > 2) {
      value = value.substring(0, 2) + '/' + value.substring(2, 4);
    }
    
    input.value = value;
    this.contractForm.patchValue({ endDate: value }, { emitEvent: false });
  }

  formatStartDate(event: Event): void {
    if (!this.isEditMode) return;
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, '');
    if (value.length > 8) value = value.substring(0, 8);
    if (value.length > 6) {
      value = value.substring(0, 2) + '/' + value.substring(2, 4) + '/' + value.substring(4, 8);
    } else if (value.length > 4) {
      value = value.substring(0, 2) + '/' + value.substring(2, 4) + '/' + value.substring(4, 6);
    } else if (value.length > 2) {
      value = value.substring(0, 2) + '/' + value.substring(2, 4);
    }
    input.value = value;
    this.contractForm.patchValue({ startDate: value }, { emitEvent: true });
  }

  generateContractNumber(): void {
    // Formato: B2B-{ano}-{sequencial}
    // Gera um número sequencial baseado no timestamp para garantir unicidade
    // Em produção, idealmente isso deveria vir do backend
    const year = this.currentYear;
    // Usa os últimos 6 dígitos do timestamp para criar um número único
    const timestamp = Date.now().toString();
    const sequencial = timestamp.slice(-6); // Últimos 6 dígitos
    const contractNumber = `B2B-${year}-${sequencial}`;
    this.contractForm.patchValue({ contractNumber });
  }


  onSubmit(): void {
    if (this.contractForm.invalid) {
      this.contractForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    const formValue = this.contractForm.getRawValue();

    // Converter datas do formato brasileiro para ISO
    const startDateISO = formValue.startDate ? this.parseBrDateToDate(formValue.startDate)?.toISOString() : null;
    const endDateISO = formValue.endDate ? this.parseBrDateToDate(formValue.endDate)?.toISOString() : null;

    const companyAddress = this.buildCompanyAddress(formValue);

    if (this.isEditMode && this.contractId) {
      const updatePayload = {
        customerId: formValue.customerId,
        documentTemplateVersionId:
          formValue.documentTemplateVersionId || this.loadedContract?.documentTemplateVersionId || 1,
        contractNumber: formValue.contractNumber,
        contractName: formValue.contractName,
        startDate: startDateISO,
        endDate: endDateISO,
        status: this.loadedContract?.status || formValue.status || 'Draft',
        cnpj: String(formValue.cnpj || '').replace(/\D/g, ''),
        companyName: formValue.companyName,
        companyAddress,
        fichaCadastralUrl: this.loadedContract?.fichaCadastralUrl || null,
        contractDocumentUrl: this.loadedContract?.contractDocumentUrl || null,
        clausesAndConditions: this.loadedContract?.clausesAndConditions || null,
        unit: this.loadedContract?.unit || null,
        salesperson: this.loadedContract?.salesperson || null,
        observations: formValue.notes || null
      };

      this.contractsService.updateB2bContract(this.contractId, updatePayload).subscribe({
        next: () => {
          this.isSubmitting = false;
          this.router.navigate(['/b2b/contracts', this.contractId, 'view']);
        },
        error: (err) => {
          this.errorService.handleHttpError(err);
          this.isSubmitting = false;
        }
      });
      return;
    }

    // Preparar dados para envio (criação)
    const contractData = {
      customerId: formValue.customerId,
      documentTemplateVersionId: formValue.documentTemplateVersionId,
      contractNumber: formValue.contractNumber,
      contractName: formValue.contractName,
      startDate: startDateISO,
      endDate: endDateISO,
      status: 'Aguardando Assinatura', // Sempre fixo
      cnpj: formValue.cnpj.replace(/\D/g, ''), // Remover formatação do CNPJ
      companyName: formValue.companyName,
      companyAddress,
      tradeName: formValue.tradeName || null,
      address: formValue.address || null,
      city: formValue.city || null,
      state: formValue.state || null,
      zipCode: formValue.zipCode || null,
      phoneNumber: formValue.phoneNumber || null,
      email: formValue.email || null,
      representativeName: formValue.representativeName || null,
      representativeCpf: formValue.representativeCpf ? formValue.representativeCpf.replace(/\D/g, '') : null,
      representativeRole: formValue.representativeRole || null,
      notes: formValue.notes || null,
      observations: formValue.notes || null,
      baseAmount: Number(formValue.baseAmount) || 0,
      depositAmount: Number(formValue.depositAmount) || 0
    };

    this.contractsService.createB2bContract(contractData).subscribe({
      next: (response) => {
        const contractId = Number(response?.id || response?.Id || 0) || null;
        this.isSubmitting = false;
        if (contractId) {
          this.router.navigate(['/b2b/contracts', contractId, 'view']);
        } else {
          this.router.navigate(['/b2b/contracts']);
        }
      },
      error: (err) => {
        this.errorService.handleHttpError(err);
        this.isSubmitting = false;
      }
    });
  }

  private buildCompanyAddress(formValue: any): string | null {
    const parts = [
      formValue.address,
      formValue.city,
      formValue.state,
      formValue.zipCode
    ]
      .map((p: unknown) => String(p || '').trim())
      .filter(Boolean);
    if (parts.length > 0) return parts.join(' - ');
    return formValue.address || this.loadedContract?.companyAddress || null;
  }

  onCancel(): void {
    if (this.isEditMode && this.contractId) {
      this.router.navigate(['/b2b/contracts', this.contractId, 'view']);
      return;
    }
    this.router.navigate(['/b2b/contracts']);
  }

  goToStep(step: number): void {
    if (step < 1 || step > this.totalSteps) return;
    this.currentStep = step;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  searchCompanies(): void {
    const term = this.companySearchTerm.trim();
    if (!term || term.length < 2) {
      this.errorService.showInfoMessage('Digite pelo menos 2 caracteres para pesquisar a empresa.');
      return;
    }
    this.isLoadingCompanySearch = true;
    this.companySearchResults = [];
    this.customersService.getAllPaged({
      page: 1,
      pageSize: 20,
      personType: 'Juridica',
      searchTerm: term
    }).subscribe({
      next: (result) => {
        this.companySearchResults = result.data || [];
        this.isLoadingCompanySearch = false;
        if (this.companySearchResults.length === 0) {
          this.errorService.showInfoMessage('Nenhuma empresa (pessoa jurídica) encontrada.');
        }
      },
      error: (err) => {
        this.errorService.handleHttpError(err);
        this.isLoadingCompanySearch = false;
      }
    });
  }

  onSelectCompany(c: Customer): void {
    const addressParts = [c.street, c.number, c.complement].filter(Boolean);
    const addressLine = addressParts.length ? addressParts.join(', ') : (c.street || '');
    this.contractForm.patchValue({
      customerId: c.id,
      companyName: c.name || '',
      tradeName: c.tradeName || '',
      cnpj: this.formatCnpjForDisplay(c.documentNumber || ''),
      email: c.email || '',
      address: addressLine,
      city: c.city || '',
      state: c.state || '',
      zipCode: c.zipCode || '',
      phoneNumber: c.primaryPhone || '',
      representativeName: c.contactPersonName || ''
    }, { emitEvent: false });
    this.companySearchResults = [];
    this.companySearchTerm = '';
    this.loadAsaasCustomerByCpf(c.documentNumber || '');
    this.errorService.showInfoMessage(`Empresa "${c.name}" selecionada. Dados preenchidos.`);
  }

  private formatCnpjForDisplay(d: string): string {
    const digits = (d || '').replace(/\D/g, '');
    if (digits.length !== 14) return d || '';
    return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  }

  private onCnpjChange(value: string): void {
    const digits = (value || '').replace(/\D/g, '');
    if (digits.length === 14) {
      this.loadAsaasCustomerByCpf(digits);
      return;
    }
    this.asaasCustomerId = null;
    this.asaasCustomerError = null;
    this.asaasCustomerInfo = null;
  }

  loadAsaasCustomerByCpf(cpfCnpj: string): void {
    const digits = (cpfCnpj || '').replace(/\D/g, '');
    if (!digits || (digits.length !== 11 && digits.length !== 14)) {
      this.asaasCustomerId = null;
      this.asaasCustomerError = null;
      this.asaasCustomerInfo = null;
      return;
    }
    this.asaasCustomerLoading = true;
    this.asaasCustomerError = null;
    this.paymentService.listAsaasCustomers({ cpfCnpj: digits }).subscribe({
      next: (res) => {
        this.asaasCustomerLoading = false;
        const first = res?.data?.[0];
        this.asaasCustomerId = first?.id ?? null;
        this.asaasCustomerInfo = first || null;
        if (!this.asaasCustomerId && Array.isArray(res?.data) && res.data.length === 0) {
          this.asaasCustomerError = 'Cliente de pagamento não encontrado para este CPF/CNPJ.';
        }
      },
      error: (err) => {
        this.asaasCustomerLoading = false;
        this.asaasCustomerId = null;
        this.asaasCustomerInfo = null;
        this.asaasCustomerError = err?.message || 'Erro ao consultar cliente de pagamento.';
      }
    });
  }

  hasDepositBoleto(): boolean {
    return this.depositPayments?.some(p => p.method === 'BOLETO') ?? false;
  }

  hasDepositCard(): boolean {
    return this.depositPayments?.some(p => p.method === 'CREDIT_CARD') ?? false;
  }

  setDepositPaymentMode(mode: 'single' | 'split'): void {
    this.depositPaymentMode = mode;
    this.recalculateDepositSplit();
  }

  setFirstInstallmentPaymentMode(mode: 'single' | 'split'): void {
    this.firstInstallmentPaymentMode = mode;
    this.recalculateFirstInstallmentSplit();
  }

  onDepositMethodChange(index: number, method: string): void {
    if (!this.depositPayments[index]) return;
    this.depositPayments[index].method = method;
    if (method === 'CREDIT_CARD') {
      this.depositPaymentMode = 'split';
      this.recalculateDepositSplit();
    }
  }

  onDepositAmountChange(index: number, value: number): void {
    const total = this.getDepositTotal();
    const v = Math.max(0, Number(value) || 0);
    if (this.depositPaymentMode === 'single') {
      this.depositPayments[0].amount = Math.min(v, total);
      this.depositPayments[1].amount = 0;
      return;
    }
    if (index === 0) {
      const first = Math.min(v, total);
      this.depositPayments[0].amount = first;
      this.depositPayments[1].amount = total - first;
    } else {
      const second = Math.min(v, total);
      this.depositPayments[1].amount = second;
      this.depositPayments[0].amount = total - second;
    }
  }

  onFirstInstallmentAmountChange(index: number, value: number): void {
    const total = this.getFirstInstallmentTotal();
    const v = Math.max(0, Number(value) || 0);
    if (this.firstInstallmentPaymentMode === 'single') {
      this.firstInstallmentPayments[0].amount = Math.min(v, total);
      this.firstInstallmentPayments[1].amount = 0;
      return;
    }
    if (index === 0) {
      const first = Math.min(v, total);
      this.firstInstallmentPayments[0].amount = first;
      this.firstInstallmentPayments[1].amount = total - first;
    } else {
      const second = Math.min(v, total);
      this.firstInstallmentPayments[1].amount = second;
      this.firstInstallmentPayments[0].amount = total - second;
    }
  }

  private initPaymentSplits(firstInstallmentTotal: number, depositTotal: number): void {
    this.depositPayments = [
      { method: this.depositPayments[0]?.method || 'CASH', amount: depositTotal },
      { method: this.depositPayments[1]?.method || 'PIX', amount: 0 }
    ];
    this.firstInstallmentPayments = [
      { method: this.firstInstallmentPayments[0]?.method || 'CASH', amount: firstInstallmentTotal },
      { method: this.firstInstallmentPayments[1]?.method || 'PIX', amount: 0 }
    ];
  }

  private getDepositTotal(): number {
    return Number(this.contractForm.get('depositAmount')?.value) || 0;
  }

  private getFirstInstallmentTotal(): number {
    return Number(this.contractForm.get('baseAmount')?.value) || 0;
  }

  private recalculateDepositSplit(): void {
    const total = this.getDepositTotal();
    if (total <= 0) {
      this.depositPayments[0].amount = 0;
      this.depositPayments[1].amount = 0;
      return;
    }
    if (this.depositPaymentMode === 'single') {
      this.depositPayments[0].amount = total;
      this.depositPayments[1].amount = 0;
      return;
    }
    let first = Number(this.depositPayments[0].amount) || 0;
    if (first <= 0 || first >= total) {
      first = Math.round((total / 2) * 100) / 100;
    }
    this.depositPayments[0].amount = first;
    this.depositPayments[1].amount = total - first;
  }

  private recalculateFirstInstallmentSplit(): void {
    const total = this.getFirstInstallmentTotal();
    if (total <= 0) {
      this.firstInstallmentPayments[0].amount = 0;
      this.firstInstallmentPayments[1].amount = 0;
      return;
    }
    if (this.firstInstallmentPaymentMode === 'single') {
      this.firstInstallmentPayments[0].amount = total;
      this.firstInstallmentPayments[1].amount = 0;
      return;
    }
    let first = Number(this.firstInstallmentPayments[0].amount) || 0;
    if (first <= 0 || first >= total) {
      first = Math.round((total / 2) * 100) / 100;
    }
    this.firstInstallmentPayments[0].amount = first;
    this.firstInstallmentPayments[1].amount = total - first;
  }

  private validatePaymentSplits(): boolean {
    const depositTotal = this.getDepositTotal();
    const depositSplitSum = this.depositPayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    if (depositTotal >= 0 && depositSplitSum > depositTotal + 0.01) {
      this.errorService.showErrorMessage('A soma dos valores da caução não pode ultrapassar o valor total da caução.');
      return false;
    }

    const firstInstallmentTotal = this.getFirstInstallmentTotal();
    const firstInstallmentSplitSum = this.firstInstallmentPayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    if (firstInstallmentTotal >= 0 && firstInstallmentSplitSum > firstInstallmentTotal + 0.01) {
      this.errorService.showErrorMessage('A soma dos valores da 1ª parcela não pode ultrapassar o valor total da 1ª parcela.');
      return false;
    }
    return true;
  }

  formatPrice(value: number): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value) || 0);
  }

  private getTodayDateString(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  private triggerAsaasPayments(createdContract: any, formValue: any): Observable<void> {
    const customerId = this.asaasCustomerId;
    if (!customerId) return of(undefined);

    const today = this.getTodayDateString();
    const startDateObj = this.parseBrDateToDate(formValue.startDate);
    const dueDate = startDateObj
      ? `${startDateObj.getFullYear()}-${String(startDateObj.getMonth() + 1).padStart(2, '0')}-${String(startDateObj.getDate()).padStart(2, '0')}`
      : today;

    const observables: Observable<unknown>[] = [];
    const arBatch: AccountsReceivableCreate[] = [];
    const customerIdForAr = Number(createdContract?.customerId ?? formValue.customerId ?? 0) || undefined;
    const contractNumberForAr = createdContract?.contractNumber?.toString?.() ?? createdContract?.id?.toString?.() ?? '';

    const onError = (msg: string) => (err: any) => {
      this.errorService.showErrorMessage(msg + (err?.error?.message || err?.message || 'Erro desconhecido'));
      return of(null);
    };

    const addAccountsReceivable = (options: {
      value: number;
      dueDate: string;
      description: string;
      externalReference?: string;
      installmentNumber?: number;
      totalInstallments?: number;
    }) => {
      if (!customerIdForAr) return;
      const docNumber = options.externalReference || `B2B-${createdContract.id}-${options.installmentNumber || 1}`;
      arBatch.push({
        contractNumber: contractNumberForAr || undefined,
        documentNumber: docNumber,
        documentType: 'B2B',
        issueDate: today,
        dueDate: options.dueDate,
        originalAmount: Number(options.value),
        amountToReceive: Number(options.value),
        status: 'Pendente',
        category: 'Venda',
        customerId: customerIdForAr,
        costCenterId: 1,
        description: options.description,
        invoiceNumber: undefined,
        saleOrderNumber: `B2B-CONTRATO-${createdContract.id}`,
        installmentNumber: options.installmentNumber,
        totalInstallments: options.totalInstallments
      });
    };

    this.firstInstallmentPayments
      .filter(p => p.amount && p.amount > 0 && (p.method === 'PIX' || p.method === 'BOLETO'))
      .forEach(p => {
        const charge: AsaasCharge = {
          customerId,
          billingType: p.method as 'PIX' | 'BOLETO',
          value: Number(p.amount),
          dueDate,
          description: `B2B - 1ª Parcela - ${p.method}`,
          externalReference: `B2B-CONTRATO-${createdContract.id}-P1-${p.method}`
        };
        addAccountsReceivable({
          value: charge.value,
          dueDate: charge.dueDate,
          description: charge.description || 'B2B - 1ª Parcela',
          externalReference: charge.externalReference
        });
        observables.push(
          this.paymentService.createAsaasCharge(charge).pipe(catchError(onError('Erro ao gerar cobrança 1ª parcela: ')))
        );
      });

    this.depositPayments
      .filter(p => p.amount && p.amount > 0 && p.method === 'PIX')
      .forEach((p, index) => {
        const charge: AsaasCharge = {
          customerId,
          billingType: 'PIX',
          value: Number(p.amount),
          dueDate,
          description: 'B2B - Caução - PIX',
          externalReference: `B2B-CONTRATO-${createdContract.id}-CAUCAO-PIX-${index + 1}`
        };
        addAccountsReceivable({
          value: charge.value,
          dueDate: charge.dueDate,
          description: charge.description || 'B2B - Caução - PIX',
          externalReference: charge.externalReference
        });
        observables.push(
          this.paymentService.createAsaasCharge(charge).pipe(catchError(onError('Erro ao gerar cobrança PIX da caução: ')))
        );
      });

    const cardDeposits = this.depositPayments.filter(p => p.amount && p.amount > 0 && p.method === 'CREDIT_CARD');
    if (cardDeposits.length > 0) {
      if (!this.asaasCustomerInfo) {
        this.errorService.showErrorMessage('Não foi possível carregar os dados do cliente para pagamento com cartão.');
      } else if (!this.creditCardNumber || !this.creditCardExpiryMonth || !this.creditCardExpiryYear || !this.creditCardCcv) {
        this.errorService.showErrorMessage('Preencha todos os dados do cartão para realizar o pagamento da caução.');
      } else {
        const customerInfo = this.asaasCustomerInfo as AsaasCustomer;
        cardDeposits.forEach((p, index) => {
          const useSecondCard = cardDeposits.length > 1 && index === 1;
          const installmentsForThisCard = Math.max(
            1,
            Math.min(10, useSecondCard ? (this.depositCardInstallments2 || 1) : (this.depositCardInstallments || 1))
          );
          const charge: AsaasCharge = {
            customerId,
            billingType: 'CREDIT_CARD',
            value: Number(p.amount),
            dueDate,
            description: `B2B - Caução - Cartão (${installmentsForThisCard}x) - parte ${index + 1}/${cardDeposits.length}`,
            externalReference: `B2B-CONTRATO-${createdContract.id}-CAUCAO-CARTAO-${index + 1}`,
            installmentCount: installmentsForThisCard,
            creditCard: {
              holderName: useSecondCard ? (this.creditCardHolderName2 || customerInfo.name) : (this.creditCardHolderName || customerInfo.name),
              number: useSecondCard ? this.creditCardNumber2 : this.creditCardNumber,
              expiryMonth: useSecondCard ? this.creditCardExpiryMonth2 : this.creditCardExpiryMonth,
              expiryYear: useSecondCard ? this.creditCardExpiryYear2 : this.creditCardExpiryYear,
              ccv: useSecondCard ? this.creditCardCcv2 : this.creditCardCcv
            },
            creditCardHolderInfo: {
              name: customerInfo.name,
              email: customerInfo.email || formValue.email,
              cpfCnpj: customerInfo.cpfCnpj,
              postalCode: customerInfo.postalCode || (formValue.zipCode || '').replace(/\D/g, ''),
              addressNumber: customerInfo.addressNumber || 'S/N',
              addressComplement: customerInfo.addressComplement || '',
              phone: customerInfo.phone || (formValue.phoneNumber || ''),
              mobilePhone: customerInfo.mobilePhone || (formValue.phoneNumber || '')
            }
          };
          addAccountsReceivable({
            value: charge.value,
            dueDate: charge.dueDate,
            description: charge.description || 'B2B - Caução - Cartão',
            externalReference: charge.externalReference
          });
          observables.push(
            this.paymentService.createAsaasCharge(charge).pipe(catchError(onError('Erro ao gerar cobrança no cartão da caução: ')))
          );
        });
      }
    }

    const boletoTotal = this.depositPayments
      .filter(p => p.amount && p.amount > 0 && p.method === 'BOLETO')
      .reduce((sum, p) => sum + Number(p.amount), 0);
    const installments = Math.max(1, Math.min(10, this.depositInstallments || 1));

    if (boletoTotal > 0 && installments === 1) {
      const charge: AsaasCharge = {
        customerId,
        billingType: 'BOLETO',
        value: boletoTotal,
        dueDate,
        description: 'B2B - Caução - Boleto à vista',
        externalReference: `B2B-CONTRATO-${createdContract.id}-CAUCAO-1`
      };
      addAccountsReceivable({
        value: charge.value,
        dueDate: charge.dueDate,
        description: charge.description || 'B2B - Caução - Boleto à vista',
        externalReference: charge.externalReference
      });
      observables.push(
        this.paymentService.createAsaasCharge(charge).pipe(catchError(onError('Erro ao gerar boleto da caução: ')))
      );
    } else if (boletoTotal > 0 && installments > 1) {
      const totalCents = Math.round(boletoTotal * 100);
      const baseCents = Math.floor(totalCents / installments);
      const depositBatch: AsaasChargeBatch[] = [];
      for (let i = 0; i < installments; i++) {
        const isLast = i === installments - 1;
        const cents = isLast ? totalCents - baseCents * i : baseCents;
        depositBatch.push({
          customerId,
          value: cents / 100,
          dueDate,
          description: `B2B - Caução - parcela ${i + 1}/${installments}`,
          externalReference: `B2B-CONTRATO-${createdContract.id}-CAUCAO-${i + 1}`
        });
      }
      depositBatch.forEach((item, index) => {
        addAccountsReceivable({
          value: item.value,
          dueDate: item.dueDate,
          description: item.description || `B2B - Caução - parcela ${index + 1}/${installments}`,
          externalReference: item.externalReference,
          installmentNumber: index + 1,
          totalInstallments: installments
        });
      });
      observables.push(
        this.paymentService.createAsaasChargeBatch(depositBatch).pipe(catchError(onError('Erro ao gerar boletos da caução: ')))
      );
    }

    if (arBatch.length > 0 && customerIdForAr) {
      observables.push(
        this.accountsReceivableService.createAccountsReceivableBatch(arBatch).pipe(catchError(onError('Erro ao criar contas a receber: ')))
      );
    }

    if (observables.length === 0) return of(undefined);
    return forkJoin(observables).pipe(
      catchError(() => of(null)),
      map(() => undefined)
    );
  }

  getFieldError(fieldName: string): string {
    const field = this.contractForm.get(fieldName);
    if (field && field.invalid && (field.touched || field.dirty)) {
      if (field.errors?.['required']) {
        return 'Campo obrigatório';
      }
      if (field.errors?.['email']) {
        return 'E-mail inválido';
      }
      if (field.errors?.['minDate']) {
        return 'Data não pode ser retroativa';
      }
      if (field.errors?.['endDateBeforeStart']) {
        return 'Data de término deve ser posterior à data de início';
      }
      if (field.errors?.['invalidDate']) {
        return 'Data inválida. Use o formato DD/MM/YYYY';
      }
    }
    return '';
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.contractForm.get(fieldName);
    return !!(field && field.invalid && (field.touched || field.dirty));
  }

  formatCnpj(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    if (value.length <= 14) {
      value = value.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
      this.contractForm.patchValue({ cnpj: value }, { emitEvent: false });
    }
  }

  formatCpf(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    if (value.length <= 11) {
      value = value.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
      this.contractForm.patchValue({ representativeCpf: value }, { emitEvent: false });
    }
  }

  formatPhone(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    if (value.length <= 11) {
      if (value.length === 11) {
        value = value.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
      } else if (value.length === 10) {
        value = value.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
      }
      this.contractForm.patchValue({ phoneNumber: value }, { emitEvent: false });
    }
  }

  formatZipCode(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    if (value.length <= 8) {
      value = value.replace(/^(\d{5})(\d{3})$/, '$1-$2');
      this.contractForm.patchValue({ zipCode: value }, { emitEvent: false });
      
      // Buscar CEP quando tiver 8 dígitos
      if (value.replace(/\D/g, '').length === 8) {
        this.lookupCep(value.replace(/\D/g, ''));
      }
    }
  }

  lookupCep(cep: string): void {
    const cepDigits = cep.replace(/\D/g, '');
    if (cepDigits.length !== 8) return;

    this.isLoadingCep = true;
    this.cepError = null;

    this.viaCepService.lookup(cepDigits).subscribe({
      next: (res: ViaCepResponse) => {
        this.isLoadingCep = false;
        if ((res as any)?.erro) {
          this.cepError = 'CEP não encontrado.';
          return;
        }
        // Preencher campos de endereço
        this.contractForm.patchValue({
          address: res.logradouro || '',
          city: res.localidade || '',
          state: res.uf || '',
          zipCode: res.cep || this.contractForm.get('zipCode')?.value
        }, { emitEvent: false });
        this.cepError = null;
      },
      error: () => {
        this.isLoadingCep = false;
        this.cepError = 'Erro ao consultar o CEP.';
      }
    });
  }

}

